import type {HideableValue, PartiallyOptional, RichTextContent} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    hideableValueCreateHidden,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce,
} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata,
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    DoppeActionListItemClickBehaviour,
    doppeListActionContentSettingsMetadataCreateForListActionCommonSettings,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithItemClickBehaviourMigration,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage,
} from '../../client-server-common';
import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction,
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResetSettingsForLock,
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResolveMainURL,
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import type {
    DoppeActionAttributesSettings,
} from '../../client-server-common/types/doppe-action-attributes-settings';
import {
    doppeActionAttributesSettingsDefaultValuesMigration,
} from '../../client-server-common/types/doppe-action-attributes-settings';
import type {
    DoppeActionContentSectionsSettings,
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration,
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {
    DoppeActionCtaButtonsSettings,
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration,
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {DoppeActionTypeDownloadFilePreview} from '../doppe-action-type-download-file-preview/doppe-action-type-download-file-preview';

/**
 * `doppeActionTypeDownloadFilesList` — a list/multi-item variant of
 * `doppeActionTypeDownloadFilePreview`. Lets a user curate a list of
 * downloadable items, each with its own file, price, attributes, title,
 * description and media; renders as a list of preview cards.
 *
 * Items render a shared Download CTA via the standard
 * `listItemTemplateCtaButtonsSettings` mechanism. The CTA is the
 * `DoppeActionCTAType.DownloadMainFile` button, auto-seeded by the factory
 * with `isRequired: true` — the editable-items list view's "locked-CTA
 * framework" then suppresses both Hide and Delete entries in the per-CTA
 * More-actions menu, so the Download button can't be removed or hidden
 * from a list whose entire purpose is to download.
 *
 * Click flow per item (when `itemClickBehaviour === OpenLink`): the viewer
 * dispatches the same flow as the template Download CTA — free items
 * download directly via a signed URL, paid items open the Buy modal →
 * Wix Checkout → thank-you modal. The shared CTA resolver looks up the
 * clicked item by `listItemId` (see
 * `doppe-action-type-widget-inner-link-type-cta-button-utils` →
 * `DoppeActionCTAType.DownloadMainFile` case) and sources file/price/
 * title/description from the item, not the parent action.
 */
export namespace DoppeActionTypeDownloadFilesList {

    /**
     * Per-item settings. Mirrors `DoppeActionTypeDownloadFilePreview`'s
     * content + download settings, plus the standard list-item base
     * (id/enabled/deleted/displayInSearch) and per-item click behaviour.
     *
     * Sections and CTA buttons are NOT per-item here — they live on the
     * list-level action settings (sections) and on
     * `listItemTemplateCtaButtonsSettings` (the shared CTA template that
     * renders the auto-seeded Download button on every item).
     */
    export interface DownloadFileItemSettings extends
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeListActionSettings.ListItemWithItemClickBehaviour {
        /**
         * The Wix-Media-Manager-bound file struct. `null` when no file is
         * selected. Structurally identical to
         * `DoppeActionTypeDownloadFilePreview.FileSettings` — same shape,
         * same all-or-nothing semantics.
         */
        fileSettings: DoppeActionTypeDownloadFilePreview.FileSettings | null;
        /**
         * Hidden = free download for this item. Visible = paid download
         * at the configured price for this item.
         */
        price: HideableValue<DoppeActionPrice>;
        /**
         * Preview images. Today the widget renders only `media[0]` —
         * array shape is forward-compat for a future gallery, mirroring
         * the single-action's pattern.
         */
        media: WixMediaResource[];
        /** Always rendered (non-hideable). Item headline. */
        title: string;
        description: HideableValue<RichTextContent>;
        /**
         * Short factual phrases displayed below the description (e.g.
         * format, file size). Hideable; defaults to hidden so newly added
         * items render unchanged until the user toggles the section on.
         */
        fileAttributes: DoppeActionAttributesSettings;
    }

    export interface ActionSettings extends
        DoppeListActionSettings<DownloadFileItemSettings>,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        // Items don't extend `ListItemWithCtaButtons` (no per-item CTA
        // overrides — the shared template IS the source of truth), so the
        // `DoppeListActionSettings<ItemSettings>` conditional doesn't pull
        // in `WithListItemTemplateCtaButtons` automatically. Add it
        // explicitly so the shared template settings are part of the
        // action's persisted shape (and the seeded Download CTA can live
        // there).
        DoppeListActionSettings.WithListItemTemplateCtaButtons,
        // Same rationale for sections — items don't extend
        // `ListItemWithSections`, so we opt in to the list-level template
        // explicitly.
        DoppeListActionSettings.WithListItemTemplateSections,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowPrice: boolean;
    }

    export type PartialProps = Partial<ActionSettings>;
}

// Per-item content metadata. Not `satisfies`-typed against
// `BymoPagePropsContentSettingsMetadata<DownloadFileItemSettings>` because
// that constraint would require entries for the `ListItemBaseSettings`
// fields (id/enabled/deleted/displayInSearch/isDummy) — those are framework
// concerns, not user-facing content, and are handled by the list-item
// metadata machinery. Mirrors how contact-card-list / contents-list expose
// their per-item content metadata.
export const doppeActionTypeDownloadFilesListActionListItemContentSettingsMetadata = {
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    media: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    fileSettings: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    price: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    fileAttributes: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
};

export const doppeActionTypeDownloadFilesList = doppeActionTypeDefineMetadata<DoppeActionTypeDownloadFilesList.ActionSettings, DoppeActionTypeDownloadFilesList.PartialProps>({
    id: 'io.bymo.action/download-files-list',
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            previewShowTitle = true,
            previewShowDescription = true,
            previewShowPrice = true,
            // Default item click behaviour = PreviewItem. Clicking an item
            // opens the inline expanded view (rendered by the widget via the
            // single `download-file-preview` action's Inner widget),
            // showing the item's image / title / description / attributes
            // plus the seeded Download CTA. The user can still flip an
            // item to `OpenLink` (immediate download) per-item via the
            // settings panel — the per-item override on
            // `itemClickBehaviour` is hideable.
            itemClickBehaviour = DoppeActionListItemClickBehaviour.PreviewItem,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            previewShowTitle: previewShowTitle,
            previewShowDescription: previewShowDescription,
            previewShowPrice: previewShowPrice,
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeDownloadFilesList.DownloadFileItemSettings>({
                ...action.settings,
                listDataItems: listDataItems.map(item => {

                    const {
                        fileSettings = null,
                        price = hideableValueCreateHidden({actualPrice: 5, currencyCode: 'USD'}),
                        media = [],
                        title = '',
                        description = hideableValueCreateHidden(''),
                        fileAttributes,
                        ...restItem
                    } = item as PartiallyOptional<DoppeActionTypeDownloadFilesList.DownloadFileItemSettings, 'fileSettings' | 'price' | 'media' | 'title' | 'description' | 'fileAttributes'>;

                    return {
                        ...restItem,
                        ...doppeListActionListItemSettingsMigrateBaseSettings(item),
                        ...doppeListActionListItemWithItemClickBehaviourMigration(item),
                        fileSettings: fileSettings,
                        price: price,
                        media: media,
                        title: title,
                        description: description,
                        fileAttributes: doppeActionAttributesSettingsDefaultValuesMigration(fileAttributes),
                    };
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings({
                itemClickBehaviour: itemClickBehaviour,
            }),
            ...doppeListActionGetMainDetailsDefaultSettings({}, action.settings),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeDownloadFilesList.ActionSettings;
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
            ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings(),
            previewShowTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowTitle'}),
            previewShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowDescription'}),
            previewShowPrice: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowPrice'}),
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {},
        },
        supportsContentSections: {
            customContentSectionsTypes: {},
        },
        hideableContentSettingsNames: {
            mainImage: true,
            mainTitle: true,
        },
        bindableContentSettingsNames: {},
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeDownloadFilesListActionListItemContentSettingsMetadata,
        // Per-item CTAs are NOT supported — every item renders the shared
        // template Download CTA from `listItemTemplateCtaButtonsSettings`.
        supportsActionCtaButtons: false,
        // Per-item sections are NOT supported — sections live at list level.
        supportsContentSections: false,
        hideableContentSettingsNames: {
            description: true,
            price: true,
            fileAttributes: true,
            itemClickBehaviour: true,
        },
        bindableContentSettingsNames: {},
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    resolveListItemsCount: actionSettings => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        // The shared `DownloadMainFile` template CTA is dispatched per-item
        // by the viewer using `listItemId`; this resolver therefore returns
        // a `null` URL for every item — the viewer's click handler picks up
        // the dispatch path (see CTA resolver `DoppeActionCTAType.DownloadMainFile`).
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            return {
                url: doppeActionCtaButtonsResolveMainURL(actionSettings, {
                    sourceLinkSpec: null,
                    viewerLang: viewerLang,
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager,
                    bymoPageContextProps: bymoPageContextProps,
                }) ?? null,
                title: item.title,
                action: action,
                description: item.description,
                image: item.media?.[0] ?? null,
                itemId: item.id,
                couponCode: hideableValueCreateHidden(''),
                ribbon: hideableValueCreateHidden(''),
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            };
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([
            ...arrayFlatten(actionSettings.listDataItems.map(itemSettings => itemSettings.media ?? [])),
            hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null,
        ]);
    },
    resolveMainMedia: actionSettings => {
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, actionSettings.listDataItems[0]?.media?.[0] ?? null);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps,
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        // Per-item CTAs aren't supported by this action — items render the
        // shared template Download CTA from `listItemTemplateCtaButtonsSettings`
        // instead. So we only reset the list-level CTAs here (no
        // per-item-CTA reset call, unlike contact-card-list / contents-list).
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.media) {
                    item.media = item.media.map(
                        image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap),
                    );
                }
            }
            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        });
    },
});
